import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { ARCHIVE } from "@/constants/testIds";
import { SITE } from "@/lib/site";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1.05, ease: [0.22, 0.61, 0.36, 1], delay: i * 0.12 },
  }),
};

const CinderArchive = () => {
  return (
    <section
      id="cinder-archive"
      data-testid={ARCHIVE.section}
      className="relative w-full py-28 md:py-40 lg:py-48"
      style={{ background: "var(--soti-surface)" }}
    >
      {/* Top and bottom hairlines tie the surface to the page */}
      <div className="absolute inset-x-0 top-0 h-px" style={{ background: "var(--soti-rule)" }} />
      <div className="absolute inset-x-0 bottom-0 h-px" style={{ background: "var(--soti-rule)" }} />

      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          custom={0}
          className="flex items-center gap-4 mb-10"
        >
          <span className="rule rule-cinder" aria-hidden="true" />
          <p className="overline">Black Salt Transmissions</p>
        </motion.div>

        <motion.h2
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          custom={1}
          data-testid={ARCHIVE.heading}
          className="font-display text-[#F3EFEA] text-4xl sm:text-5xl md:text-6xl leading-[1.04] tracking-[-0.02em] max-w-3xl"
        >
          Enter the Cinder Archive.
        </motion.h2>

        <motion.p
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          custom={2}
          data-testid={ARCHIVE.body}
          className="mt-10 max-w-2xl text-[#D9D4CD] text-base sm:text-lg leading-relaxed font-light"
        >
          The Cinder Archive is the reader doorway into{" "}
          <em className="font-display italic text-[#F3EFEA]">Shadows of the Infinite</em>:
          a fictional archive of Black Salt transmissions, restricted fragments,
          behind-the-scenes updates, and early launch alerts connected to{" "}
          <em className="font-display italic text-[#F3EFEA]">Book I: Echoes of Cinder</em>.
        </motion.p>

        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          custom={3}
          className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6"
        >
          <a
            data-testid={ARCHIVE.cta}
            href={SITE.archiveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-cinder"
          >
            Enter the Cinder Archive
            <ArrowRight size={16} strokeWidth={1.4} />
          </a>
        </motion.div>

        <motion.p
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          custom={4}
          data-testid={ARCHIVE.safety}
          className="mt-10 max-w-2xl text-[#A19D99] text-sm leading-relaxed italic font-display"
        >
          Shadows of the Infinite is a fictional science-fantasy universe.
          Archive transmissions and launch updates are real opt-in emails. No
          spam. Unsubscribe anytime.
        </motion.p>
      </div>
    </section>
  );
};

export default CinderArchive;
